import { useEffect, useState } from "react";
import { HelpLang, useHelpLang } from "@/components/help/HelpLang";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { checkFiles, uploadFile } from "./SupportAttachments";

const EMPTY = { type: "site_change", category: "other", title: "", text: "", site_id: null };

// The form state lives here, not in the translations: switching language re-renders labels and keeps what was typed.
export function NewRequestDialog({ open, onOpenChange, tid, types, categories, maxFiles = 3, preset, onCreated }) {
  const api = useApi();
  const { t, lang } = useHelpLang();
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ ...EMPTY, ...(preset || {}) }); setFiles([]); } }, [open, preset]);
  const pick = (e) => {
    const list = [...e.target.files]; e.target.value = "";
    const bad = checkFiles(list, files.length, maxFiles, t);
    if (bad) return toast.error(bad);
    setFiles([...files, ...list]);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/t/${tid}/support-requests`, f);
      let failed = false;
      for (const file of files) { try { await uploadFile(api, tid, data.id, file); } catch { failed = true; } }
      failed ? toast.warning(t("dialog.upload_failed")) : toast.success(t("dialog.sent"));
      onCreated();
    } catch (e) { toast.error(`${t("dialog.failed")}: ${errMsg(e)}`); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg" data-testid="support-dialog">
        <DialogHeader className="text-left"><div className="flex flex-wrap items-center justify-between gap-2 pr-6"><DialogTitle>{t("dialog.title")}</DialogTitle><HelpLang lang={lang} /></div><DialogDescription>{t("dialog.desc")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">{Object.keys(types).map((k) => (
            <Button key={k} type="button" variant={f.type === k ? "default" : "outline"} className="h-11 px-2 text-xs" onClick={() => setF({ ...f, type: k })} data-testid={`support-type-${k}`}>{t(`req.type.${k}`)}</Button>))}</div>
          <div className="space-y-1.5"><Label htmlFor="sr-cat">{t("dialog.category")}</Label>
            <select id="sr-cat" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="support-category">
              {Object.entries(categories).map(([k, l]) => <option key={k} value={k}>{l}</option>)}<option value="other">{t("cat_other")}</option></select></div>
          <div className="space-y-1.5"><Label htmlFor="sr-title">{t("dialog.title_label")}</Label><Input id="sr-title" value={f.title} maxLength={140} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={t("dialog.title_ph")} className="h-11" data-testid="support-title" /></div>
          <div className="space-y-1.5"><Label htmlFor="sr-text">{t("dialog.text_label")}</Label><Textarea id="sr-text" rows={5} value={f.text} maxLength={5000} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="support-text" /></div>
          <div className="space-y-1.5">
            <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 text-sm text-slate-700 hover:bg-slate-50">
              <ImagePlus size={16} />{t("dialog.attach")}<input type="file" accept="image/png,image/jpeg,image/webp" multiple className="sr-only" onChange={pick} data-testid="support-files" /></label>
            <p className="text-xs text-slate-500">{t("dialog.attach_hint", { n: maxFiles })}</p>
            {files.map((x, i) => <p key={`${x.name}-${i}`} className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-700" data-testid={`support-file-${i}`}>
              <span className="truncate">{x.name}</span><button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={t("dialog.remove")} className="flex h-8 w-8 items-center justify-center rounded hover:bg-slate-200"><X size={14} /></button></p>)}
          </div>
        </div>
        <DialogFooter><Button className="h-11" disabled={busy || f.title.trim().length < 3 || f.text.trim().length < 3} onClick={save} data-testid="support-save">{busy && <Loader2 size={15} className="mr-1.5 animate-spin" />}{t("dialog.send")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>);
}
